import { useCallback, useEffect, useState } from 'react';
import { Banknote, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import StatusPill from '../components/StatusPill';
import { primaryButtonClass } from '../components/FilterBar';
import { formatCurrency, formatDate } from '../lib/format';
import {
  fetchMercuryTransactions,
  syncMercury,
  type MercurySyncResponse,
} from '../lib/api';

type Row = Record<string, unknown>;

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '') return String(value);
  }
  return null;
}

const columns: Column<Row>[] = [
  {
    key: 'date',
    header: 'Date',
    render: (r) => formatDate(str(r, 'postedAt', 'createdAt', 'date')),
  },
  {
    key: 'description',
    header: 'Description',
    render: (r) => str(r, 'note', 'externalMemo', 'bankDescription', 'description') ?? '—',
  },
  {
    key: 'counterparty',
    header: 'Counterparty',
    render: (r) => str(r, 'counterpartyName', 'counterpartyNickname') ?? '—',
  },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (r) => {
      const amount = str(r, 'amount');
      return (
        <span className={Number(amount) < 0 ? 'text-zt-danger' : 'text-zt-primary'}>
          {formatCurrency(amount)}
        </span>
      );
    },
  },
  {
    key: 'status',
    header: 'Status',
    render: (r) => <StatusPill status={str(r, 'status') ?? 'posted'} />,
  },
];

function Mercury() {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<MercurySyncResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const result = await fetchMercuryTransactions(undefined, 50, 0);
    if (result.ok && result.data) {
      setRows(result.data.data ?? []);
      setTotal(result.data.total ?? result.data.data?.length ?? 0);
    } else {
      setRows([]);
      setTotal(0);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    const result = await syncMercury({});
    if (result.ok && result.data) {
      setLastSync(result.data);
      if (result.data.status === 'ok') {
        toast.success(`Synced ${result.data.synced ?? 0} transactions`);
      } else {
        toast(result.data.status === 'not_configured' ? 'Mercury is not configured' : `Sync: ${result.data.status}`);
      }
      await load();
    }
    setSyncing(false);
  };

  return (
    <PageShell
      title="Mercury"
      subtitle="Bank account balances, transactions, and sync status from Mercury."
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Transactions"
          value={loading ? '…' : String(total)}
          tone="info"
          icon={<Banknote size={18} />}
        />
        <StatCard
          label="Last sync status"
          value={lastSync ? (lastSync.status === 'ok' ? 'Healthy' : lastSync.status) : '—'}
          tone={lastSync?.status === 'ok' ? 'success' : lastSync ? 'warning' : 'neutral'}
        />
        <StatCard label="Synced" value={lastSync ? String(lastSync.synced ?? 0) : '—'} tone="neutral" />
        <StatCard
          label="Reconciled"
          value={lastSync ? String(lastSync.reconciled ?? 0) : '—'}
          tone="neutral"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-zt-muted">
          {lastSync?.account_count ? `${lastSync.account_count} account(s)` : 'Bank transactions synced from Mercury.'}
        </div>
        <button type="button" onClick={handleSync} disabled={syncing} className={primaryButtonClass}>
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> Sync now
        </button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r, i) => str(r, 'id') ?? String(i)}
        loading={loading}
        emptyTitle="No transactions"
        emptyMessage="Run a sync to pull the latest Mercury transactions, or connect the Mercury integration."
        emptyIcon={<Banknote size={32} />}
      />
    </PageShell>
  );
}

export default Mercury;
